import { useRef, useState, forwardRef, useImperativeHandle } from "react";
import { toast } from "sonner";
import { Upload, Loader2, ArrowRight, Sparkles, FileText } from "lucide-react";
import * as XLSX from "xlsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";

const norm = (h) => (h || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const HMAP = {
  guestname: "guest_name", guest_name: "guest_name",
  bookingstatus: "booking_status", status: "booking_status",
  tourdate: "tour_date", date: "tour_date", dates: "tour_date",
  resortmeet: "resort_meet", resort: "resort_meet", meet: "resort_meet",
  meetingtime: "meeting_time", meettime: "meeting_time",
  return: "return_trip", returntrip: "return_trip",
  days: "days", partysize: "party_size", party: "party_size",
  coguide: "co_guide", type: "tour_type", tourtype: "tour_type",
  costtype: "cost_type", costhour: "cost_per_hour", costperhour: "cost_per_hour",
  totalhours: "total_hours", comptime: "comp_time", rating: "rating",
  gratuity: "gratuity", tip: "tip_percent", tippercent: "tip_percent",
  tiptype: "tip_type", avggratuityday: "avg_per_guest", avgperguest: "avg_per_guest",
  guestfood: "guest_food", viewings: "viewings", hostagain: "host_again",
  retailvalue: "retail_value", notes: "notes", linkedguests: "linked_guests",
  city: "city", state: "state", country: "country",
  email: "mdx_email", mdxemail: "mdx_email", phone: "phone",
};
const FIELD_LABEL = {
  guest_name: "Guest name", booking_status: "Booking status", tour_date: "Tour date",
  resort_meet: "Resort / meet", meeting_time: "Meeting time", return_trip: "Return trip",
  days: "Days", party_size: "Party size", co_guide: "Co-guide", tour_type: "Type",
  cost_type: "Cost type", cost_per_hour: "Cost/hour", total_hours: "Total hours",
  comp_time: "Comp time", rating: "Rating", gratuity: "Gratuity", tip_percent: "Tip %",
  tip_type: "Tip type", avg_per_guest: "Avg gratuity/day", guest_food: "Guest food",
  viewings: "Viewings", host_again: "Host again", retail_value: "Retail value",
  notes: "Notes", linked_guests: "Linked guests", city: "City", state: "State",
  country: "Country", mdx_email: "Email", phone: "Phone",
};
const NUM = new Set(["days", "party_size", "cost_per_hour", "total_hours", "comp_time", "rating", "gratuity", "tip_percent", "avg_per_guest", "retail_value"]);
const BOOL = new Set(["return_trip", "host_again"]);
const LIST = new Set(["tour_date", "viewings", "linked_guests"]);
const PROFILE = new Set(["city", "state", "country", "mdx_email", "phone"]);

function parseCsv(text) {
  const rows = [];
  let row = [], field = "", inQuotes = false;
  text = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQuotes = false; }
      else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
    else field += c;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.length > 1 || (r[0] && r[0].trim()));
}

function coerce(key, raw) {
  let val = (raw ?? "").toString().trim();
  if (!val) return undefined;
  if (LIST.has(key)) { val = val.split(/[;,]/).map((s) => s.trim()).filter(Boolean); return val.length ? val : undefined; }
  if (NUM.has(key)) { const n = parseFloat(val.replace(/[^0-9.\-]/g, "")); return isNaN(n) ? undefined : n; }
  if (BOOL.has(key)) return /^(y|yes|true|1)/i.test(val);
  return val;
}

// Turn a rows array (from CSV/Excel) into structured items IF it's a clean table
// with a recognizable Guest Name column. Returns null when it isn't a clean table.
function tableItemsFromRows(rows) {
  if (rows.length < 2) return null;
  const headers = rows[0].map((h) => (h ?? "").toString().trim());
  const keys = headers.map((h) => HMAP[norm(h)] || null);
  if (!keys.includes("guest_name")) return null;
  const items = [];
  for (let r = 1; r < rows.length; r++) {
    const cells = rows[r] || [];
    if (cells.every((c) => c === undefined || c === null || !c.toString().trim())) continue;
    const fields = {}, profile = {};
    keys.forEach((key, ci) => {
      if (!key) return;
      const v = coerce(key, cells[ci]);
      if (v === undefined) return;
      if (PROFILE.has(key)) profile[key] = v; else fields[key] = v;
    });
    if (!fields.guest_name) continue;
    items.push({ fields, profile });
  }
  return items.length ? { headers, keys, items } : null;
}

function rowsToTsv(rows) {
  return rows.map((r) => (r || []).map((c) => (c ?? "").toString()).join("\t")).join("\n");
}

const dateStr = (d) => (Array.isArray(d) ? d.join(", ") : d || "");

export const CsvImport = forwardRef(({ onImported, embedded = false }, ref) => {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [preview, setPreview] = useState(null); // { mode, headers?, keys?, items }

  const readRows = async (file) => {
    const name = file.name.toLowerCase();
    if (name.endsWith(".csv")) return parseCsv(await file.text());
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    return XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
  };

  const processFile = async (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    setReading(true);
    try {
      if (name.endsWith(".csv") || name.endsWith(".xlsx") || name.endsWith(".xls")) {
        let rows;
        try { rows = await readRows(file); } catch { toast.error("Couldn't read that spreadsheet."); return; }
        const table = tableItemsFromRows(rows);
        if (table) {
          setPreview({ mode: "table", ...table });
          return;
        }
        // Not a clean table → auto-detect the tours from the sheet text.
        toast.info("No clear columns found — reading it automatically…");
        const res = await api.importExtractText(rowsToTsv(rows));
        setPreview({ mode: "ai", items: res.items.map((i) => ({ ...i, _include: true })) });
      } else if (name.endsWith(".docx") || name.endsWith(".pdf")) {
        toast.info("Reading your document…");
        const res = await api.importExtractFile(file);
        setPreview({ mode: "ai", items: res.items.map((i) => ({ ...i, _include: true })) });
      } else {
        toast.error("Unsupported file. Use CSV, Excel, Word, or PDF.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't read that file.");
    } finally {
      setReading(false);
    }
  };

  useImperativeHandle(ref, () => ({ processFile }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    await processFile(file);
  };

  const setName = (idx, value) => {
    setPreview((p) => {
      const items = p.items.slice();
      items[idx] = { ...items[idx], fields: { ...items[idx].fields, guest_name: value } };
      return { ...p, items };
    });
  };
  const toggleInclude = (idx) => {
    setPreview((p) => {
      const items = p.items.slice();
      items[idx] = { ...items[idx], _include: !items[idx]._include };
      return { ...p, items };
    });
  };

  const confirmImport = async () => {
    if (!preview) return;
    const toImport = preview.mode === "ai"
      ? preview.items.filter((i) => i._include && (i.fields.guest_name || "").trim()).map(({ _include, ...rest }) => rest)
      : preview.items;
    if (!toImport.length) { toast.error("Nothing selected to import."); return; }
    setBusy(true);
    try {
      const res = await api.bulkCreate(toImport);
      const n = res.inserted ?? toImport.length;
      toast.success(`Imported ${n} tour${n > 1 ? "s" : ""}`);
      setPreview(null);
      onImported?.(n, res.batch_id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  const aiCount = preview?.mode === "ai" ? preview.items.filter((i) => i._include).length : 0;

  return (
    <>
      <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls,.docx,.pdf" onChange={onFile} className="hidden" data-testid="csv-import-input" />
      {!embedded && (
        <>
          <button
            onClick={() => inputRef.current?.click()}
            disabled={reading}
            className="flex w-full items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3 text-sm text-zinc-200 transition-colors hover:bg-zinc-800 disabled:opacity-60"
            data-testid="csv-import-btn"
          >
            <span className="flex items-center gap-2">
              {reading ? <Loader2 className="h-4 w-4 animate-spin text-[#E0575B]" /> : <Upload className="h-4 w-4 text-[#E0575B]" />}
              {reading ? "Reading file…" : "Import tours from a file"}
            </span>
            <span className="text-xs text-zinc-400">CSV · Excel · Word · PDF</span>
          </button>
          <p className="mt-1.5 flex items-center gap-1.5 px-1 text-xs text-zinc-500" data-testid="csv-import-hint">
            <Sparkles className="h-3 w-3 text-[#E0575B]" /> Clean tables import instantly; notes &amp; documents are read automatically.
          </p>
        </>
      )}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="grid-cols-[minmax(0,1fr)] max-h-[85vh] w-[calc(100vw-1rem)] !max-w-lg overflow-y-auto border-zinc-800 bg-zinc-950 p-4" data-testid="csv-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display tracking-tight">
              {preview?.mode === "ai" ? <Sparkles className="h-5 w-5 text-[#E0575B]" /> : <Upload className="h-5 w-5 text-[#E0575B]" />}
              {preview?.mode === "ai" ? "Review detected tours" : "Confirm import"}
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              {preview?.mode === "ai"
                ? `Found ${preview?.items.length} tour${preview?.items.length === 1 ? "" : "s"}. Uncheck any you don't want, fix names, then import.`                : `${preview?.items.length} row${preview?.items.length === 1 ? "" : "s"} ready. Check the column mapping below before importing.`}
            </DialogDescription>
          </DialogHeader>

          {preview?.mode === "table" && (
            <div className="space-y-4">
              <div>
                <p className="mb-1.5 text-xs font-600 uppercase tracking-wide text-zinc-300">Column mapping</p>
                <div className="space-y-1">
                  {preview.headers.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900/60 px-2.5 py-1.5 text-sm" data-testid={`csv-map-${i}`}>
                      <span className="min-w-0 flex-1 truncate text-zinc-200">{h || <em className="text-zinc-600">(blank)</em>}</span>
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-zinc-600" />
                      {preview.keys[i] ? (
                        <span className="shrink-0 rounded bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-300">{FIELD_LABEL[preview.keys[i]]}</span>
                      ) : (
                        <span className="shrink-0 rounded bg-zinc-800 px-2 py-0.5 text-xs text-zinc-400">Skipped</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {preview?.mode === "ai" && (
            <div className="space-y-2" data-testid="ai-preview-list">
              {preview.items.map((it, i) => {
                const f = it.fields || {};
                const chips = [
                  dateStr(f.tour_date), f.resort_meet, f.tour_type,
                  f.party_size ? `${f.party_size} pax` : "", f.total_hours ? `${f.total_hours}h` : "",
                  f.gratuity ? `$${f.gratuity}` : "",
                ].filter(Boolean);
                return (
                  <div key={i} className={`rounded-lg border px-3 py-2.5 transition-colors ${it._include ? "border-zinc-700 bg-zinc-900" : "border-zinc-800 bg-zinc-900/30 opacity-50"}`} data-testid={`ai-item-${i}`}>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={it._include} onChange={() => toggleInclude(i)} className="h-4 w-4 shrink-0 accent-[#9B2226]" data-testid={`ai-item-toggle-${i}`} />
                      <Input
                        value={f.guest_name || ""}
                        onChange={(e) => setName(i, e.target.value)}
                        placeholder="Guest name"
                        className="h-8 border-zinc-700 bg-zinc-950 text-sm text-zinc-100"
                        data-testid={`ai-item-name-${i}`}
                      />
                    </div>
                    {chips.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1 pl-6">
                        {chips.map((c, ci) => (
                          <span key={ci} className="rounded bg-zinc-800 px-1.5 py-0.5 text-[11px] text-zinc-300">{c}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {preview && (
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setPreview(null)} className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800" data-testid="csv-cancel-btn">Cancel</Button>
              <Button onClick={confirmImport} disabled={busy || (preview.mode === "ai" && aiCount === 0)} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="csv-confirm-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <span className="flex items-center gap-1.5">
                    <FileText className="h-4 w-4" />
                    Import {preview.mode === "ai" ? aiCount : preview.items.length} tour{(preview.mode === "ai" ? aiCount : preview.items.length) === 1 ? "" : "s"}
                  </span>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
});
